import React from 'react';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import TextField from '@material-ui/core/TextField';
import trackAnalytics from '../../utils/gaia/trackAnalytics';
import {HikeField} from './HikeField';
import experiments from '../../utils/gaia/experiments';

/**
 * EditHikeInfoDialog component for editing a multi-section description for a hike or park.
 * Material-UI Dialog.
 *
 * ---------------------------
 * | Cancel   Title   Buttons |
 * ---------------------------
 * | TextField                |
 * |                          |
 * | TextField                |
 * |                          |
 * | + Add More TextFields    |
 * ---------------------------
 */

export interface EditHikeInfoDialogProps {
    open: boolean;
    onRequestClose: () => void; // Method to hide this dialog.
    regionType: 'park' | 'hike';
    regionId: number; // The id of the park or hike.
    initialStaticDescription: HikeField[];
    originalText: HikeField[];
    afterUpdate: (fields: HikeField[]) => void;
    title: string;
}

export interface EditHikeInfoDialogState {
    didEdit: boolean;
    infoInFields: HikeField[];
}

export default class EditHikeInfoDialog
    extends React.Component<EditHikeInfoDialogProps, EditHikeInfoDialogState> {

    constructor(props: EditHikeInfoDialogProps) {
        super(props);
        this.state = {
            didEdit: false,
            infoInFields: props.initialStaticDescription,
        };
    }

    // called when the user types into the TextField
    handleTextEdit(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>, i: number) {
        const infoInFields = this.state.infoInFields;

        infoInFields[i]['text'] = event.target.value;
        this.setState({
            didEdit: true,
            infoInFields,
        });
    }

    // called when the user presses Save - posts to Server and updates description on page
    saveText() {
        const url = `/api/trip/update/${this.props.regionType}/${this.props.regionId}/`;
        const removedInfo = this.state.infoInFields.slice(0);
        for (const info of removedInfo) {
            delete info.placeholder;
            delete info.hint;
        }
        const data = {description: JSON.stringify(this.state.infoInFields)};

        fetch(url,
            {
                body: JSON.stringify(data),
                method: 'POST',
                credentials: 'same-origin'
            }
        )
            .then(response => {
                return response.json();
            })
            .then(myJson => {
                if (!myJson.success && myJson.errors) {
                    console.error(myJson);
                }
            });

        // if the user deleted the title/description, use the original machine generated one
        const infoInFields = this.state.infoInFields;
        for (const field of infoInFields) {
            if (!field['text'] || !field['text'].length) {
                for (const originalField of this.props.originalText) {
                    // eslint-disable-next-line max-depth
                    if ((field['id'] !== undefined && originalField['id'] === field['id']) ||
                        (field['title'] === 'Overview' && originalField['title'] === 'Overview')) {
                        field['text'] = originalField['text'];
                        break;
                    }
                }
            }
        }
        this.setState({
            infoInFields,
        });

        experiments.goal('save description');
        trackAnalytics('Save Description', {
            category: 'Hike',
            regionId: this.props.regionId,
            regionType: this.props.regionType,
            path: window.location.pathname,
        });
        this.props.afterUpdate(this.state.infoInFields);
        this.props.onRequestClose();
    }

    render() {
        const actions = [
            <Button key={0} variant="text" onClick={this.props.onRequestClose}>Cancel</Button>,
            <Button
                key={1}
                variant="text"
                color='primary'
                onClick={() => this.saveText()}
                disabled={!this.state.didEdit}
            >Save
            </Button>,
        ];

        let textFields = [];
        textFields = this.state.infoInFields.map((fieldInfo, index) => {
            return (
                <div key={index} style={{marginBottom: 20}} id="infoEditForm">
                    <h4 style={{marginBottom: 4}}>{fieldInfo.title}</h4>
                    <TextField
                        variant='outlined'
                        margin='dense'
                        value={fieldInfo.text || ''}
                        placeholder={fieldInfo.placeholder}
                        onChange={e => this.handleTextEdit(e, index)}
                        id={`${fieldInfo.title}TextField`}
                        helperText={<span>{fieldInfo.hint}</span>}
                        multiline={true}
                        rows={5}
                        rowsMax={50}
                        fullWidth
                    />
                </div>
            );
        });

        return (
            <Dialog
                open={this.props.open}
                onClose={this.props.onRequestClose}
                maxWidth={false}
            >
                <DialogTitle className="dialog-title">{this.props.title}</DialogTitle>
                <DialogContent>
                    <div style={{marginTop: 20, width: '60vw'}}>
                        {textFields}
                    </div>
                </DialogContent>
                <DialogActions>
                    {actions}
                </DialogActions>
            </Dialog>
        );
    }
}
